import { ServicesContext, useServices } from '@abb/services';
import { useDisclosure, useToast } from '@abb/uikit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import useSWRImmutable from 'swr/immutable';
import useSWRMutation from 'swr/mutation';
import { services } from './config/services';
import { TransferAmountWarning } from './utils/transferAmountWarning';
export function useDependencies() {
    return React.useContext(ServicesContext);
}
const swrOptions = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    revalidateIfStale: false
};
export const useUserData = ()=>{
    const { userService } = useDependencies();
    return useSWR([
        'user-data',
        userService === null || userService === void 0 ? void 0 : userService.getShouldRefetch
    ], async ()=>await Promise.allSettled([
            userService === null || userService === void 0 ? void 0 : userService.getUserInfo(),
            userService === null || userService === void 0 ? void 0 : userService.getPermissions(),
            userService === null || userService === void 0 ? void 0 : userService.getProcessClaimsInfo()
        ]), {
        ...swrOptions,
        onSuccess: (data)=>{
            if (data[0].status === 'fulfilled' && data[1].status === 'fulfilled' && data[2].status === 'fulfilled') {
                userService === null || userService === void 0 ? void 0 : userService.setUserData(data[0].value);
                userService === null || userService === void 0 ? void 0 : userService.setPermissionsData(data[1].value);
                userService === null || userService === void 0 ? void 0 : userService.setProcessClaims(data[2].value);
                localStorage.setItem('selectedCif', data[2].value.customerId);
            }
        }
    });
};
export const useCountDown = (mins)=>{
    const [secs, decrement] = useState(mins * 60);
    const [progress, increment] = useState(0);
    useEffect(()=>{
        if (secs > 0) {
            const progressLevel = setInterval(()=>{
                increment(progress + 100 / (mins * 60));
                decrement(secs - 1);
            }, 1000);
            return ()=>clearInterval(progressLevel);
        }
    }, [
        progress,
        secs,
        mins
    ]);
    return [
        progress
    ];
};
export function useAccounts() {
    const { httpClient, userService } = useDependencies();
    return useSWR([
        services.account,
        userService === null || userService === void 0 ? void 0 : userService.getShouldRefetch
    ], (param)=>{
        let [url] = param;
        return httpClient.get(url);
    }, swrOptions);
}
export const useAccountLimits = (accountNo)=>{
    const { httpClient } = useDependencies();
    return useSWR(accountNo ? `${services.account}/${accountNo}/remained-limits` : null, httpClient.get, swrOptions);
};
export const useShowMore = (array, size)=>{
    const hasMore = array.length > size;
    const { isOpen: isCollapsed, onToggle: toggle } = useDisclosure({
        defaultIsOpen: true
    });
    const data = array.slice(0, isCollapsed ? size : array.length);
    return {
        isCollapsed,
        hasMore,
        data,
        toggle,
        count: array.length - size
    };
};
export const useTransferAmountWarning = function(amount, availableBalance) {
    let vatIncluded = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false, vatAmount = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : 0;
    const [amountWarning, setAmountWarning] = useState(false);
    useEffect(()=>{
        const warning = TransferAmountWarning(amount, availableBalance, vatIncluded, vatAmount);
        setAmountWarning(warning);
    }, [
        amount,
        availableBalance,
        vatIncluded,
        vatAmount
    ]);
    return amountWarning;
};
export const useReferralPromoCode = ()=>{
    const [response, setResponse] = useState({
        invitationMail: '',
        promoCodeWithLink: '',
        isRedirectBundle: false,
        showCongratsBox: false,
        showInvitationBox: false
    });
    const navigate = useNavigate();
    const { httpClient } = useDependencies();
    const getSessionData = sessionStorage.getItem('promoCodeApi');
    const getLocalData = localStorage.getItem('promoCodeApi');
    const isExists = getSessionData && getLocalData;
    const { data } = useSWR(`${services.referral}/referral`, httpClient.get, swrOptions);
    useEffect(()=>{
        let timerId = null;
        if (!getSessionData && !getLocalData && data) {
            timerId = setTimeout(()=>{
                sessionStorage.setItem('promoCodeApi', JSON.stringify(data));
                localStorage.setItem('promoCodeApi', JSON.stringify(data));
                setResponse({
                    ...data
                });
            }, 3000);
            if (data.isRedirectBundle) {
                navigate('/tariff-packages');
            }
        }
        if (isExists) {
            sessionStorage.setItem('promoCodeApi', JSON.stringify({
                ...data,
                isRedirectBundle: false,
                showCongratsBox: false,
                showInvitationBox: false
            }));
            localStorage.setItem('promoCodeApi', JSON.stringify({
                ...data,
                isRedirectBundle: false,
                showCongratsBox: false,
                showInvitationBox: false
            }));
            setResponse({
                ...data,
                isRedirectBundle: false,
                showCongratsBox: false,
                showInvitationBox: false
            });
        }
        return ()=>{
            clearTimeout(timerId);
        };
    }, [
        data
    ]);
    return {
        ...response
    };
};
export const useTrackReferral = ()=>{
    const { httpClient } = useDependencies();
    const getCounter = async ()=>{
        await httpClient.post(`${services.referral}/referral/share/counter`, undefined);
    };
    return {
        getCounter
    };
};
export const useCampaignNews = ()=>{
    const { httpClient, userService } = useDependencies();
    return useSWR([
        `${services.notification_ms}/user/campaigns/next`,
        userService === null || userService === void 0 ? void 0 : userService.getShouldRefetch
    ], (param)=>{
        let [url] = param;
        return httpClient.get(url);
    }, swrOptions);
};
export const useCheckFatca = ()=>{
    const { httpClient, userService } = useDependencies();
    return useSWR([
        `${services.account_opening}/renew-fatca/status`,
        userService === null || userService === void 0 ? void 0 : userService.getShouldRefetch
    ], (param)=>{
        let [url] = param;
        return httpClient.get(url);
    }, swrOptions);
};
export const useVideos = ()=>{
    const { httpClient } = useDependencies();
    const location = useLocation();
    const arr = location.pathname.split('/');
    const type = arr[arr.length - 1] === 'statements' ? 'pos/statements' : arr[1];
    return useSWR([
        `${services.banner_ms}/tutorials/groups`,
        type
    ], (param)=>{
        let [url] = param;
        return httpClient.get(url, {
            params: {
                type
            }
        });
    }, swrOptions);
};
export const useGetTradePlatformStatus = ()=>{
    const { httpClient, userService } = useDependencies();
    return useSWR([
        `${services.dashboard_ms}/popup/TRADE`,
        userService === null || userService === void 0 ? void 0 : userService.getShouldRefetch
    ], (param)=>{
        let [url] = param;
        return httpClient.get(url);
    }, swrOptions);
};
export const useTradePlatformStatusMutation = (param)=>{
    let { onSuccess } = param;
    const { httpClient } = useDependencies();
    const toast = useToast();
    const { t } = useTranslation('common');
    return useSWRMutation(`${services.dashboard_ms}/popup/TRADE`, (url, param)=>{
        let { arg } = param;
        return httpClient.post(url, {
            answer: arg
        });
    }, {
        onSuccess: ()=>{
            toast({
                title: t('trade-platform.apply-success-message'),
                position: 'top',
                status: 'success',
                duration: 6000,
                isClosable: true
            });
            onSuccess();
        },
        onError: ()=>{
            toast({
                title: t('errors.system'),
                position: 'top',
                status: 'error',
                duration: 6000,
                isClosable: true
            });
        }
    });
};
export const useSalaryCardWhiteList = ()=>{
    const { httpClient, userService } = useDependencies();
    return useSWR([
        `${services.salary_cards}/properties/white-list`,
        userService === null || userService === void 0 ? void 0 : userService.getShouldRefetch
    ], (param)=>{
        let [url] = param;
        return httpClient.get(url);
    }, swrOptions);
};
export const useGetNotificationList = ()=>{
    const { httpClient, languageService, userService } = useDependencies();
    return useSWR([
        `${services.notification_ms}/user/notifications/WEB`,
        languageService === null || languageService === void 0 ? void 0 : languageService.currentLanguage,
        userService === null || userService === void 0 ? void 0 : userService.getShouldRefetch
    ], (param)=>{
        let [url] = param;
        return httpClient.get(url);
    }, swrOptions);
};
export const useGetCampaignList = ()=>{
    const { httpClient, languageService, userService } = useDependencies();
    return useSWR([
        `${services.notification_ms}/user/campaigns`,
        languageService === null || languageService === void 0 ? void 0 : languageService.currentLanguage,
        userService === null || userService === void 0 ? void 0 : userService.getShouldRefetch
    ], (param)=>{
        let [url] = param;
        return httpClient.get(url);
    }, swrOptions);
};
export const useGetLoyaltyList = ()=>{
    const { httpClient, languageService } = useDependencies();
    return useSWRImmutable([
        'api/loyalty-processing-ms/v1/view/popup',
        languageService === null || languageService === void 0 ? void 0 : languageService.currentLanguage
    ], (param)=>{
        let [url] = param;
        return httpClient.get(url);
    }, {
        onSuccess (data) {
            localStorage.setItem('loyalty', `${data === null || data === void 0 ? void 0 : data.isRegistered}`);
        }
    });
};
export const useLoyaltyCheck = (type)=>{
    const { httpClient, languageService, userService } = useDependencies();
    return useSWR([
        !!type && `api/loyalty-processing-ms/v1/view/element/check?elementName=${type}`,
        languageService === null || languageService === void 0 ? void 0 : languageService.currentLanguage,
        userService === null || userService === void 0 ? void 0 : userService.getShouldRefetch
    ], (param)=>{
        let [url] = param;
        return !!type && httpClient.post(url, {});
    }, swrOptions);
};
export const useLoyaltyCheckMember = ()=>{
    const { httpClient, languageService } = useDependencies();
    return useSWRImmutable([
        'api/loyalty-processing-ms/v1/view/check-member',
        languageService === null || languageService === void 0 ? void 0 : languageService.currentLanguage
    ], (param)=>{
        let [url] = param;
        return httpClient.get(url);
    });
};
export const useLoyaltyRegister = (closeModal)=>{
    const { httpClient } = useDependencies();
    const navigate = useNavigate();
    const { t } = useTranslation('common');
    const toast = useToast();
    const isRegister = async ()=>{
        try {
            await httpClient.post(`${services.loyalty}/customer/registration`, {});
            navigate(0);
            await closeModal();
        } catch (error) {
            toast({
                title: t('errors.system'),
                position: 'top',
                status: 'error',
                duration: 6000,
                isClosable: true,
                id: 'emptyFields'
            });
        }
    };
    return {
        isRegister
    };
};
export const useCampaignsRead = (id)=>{
    const { httpClient } = useDependencies();
    const { t } = useTranslation('common');
    const toast = useToast();
    const read = async (mutateFn)=>{
        try {
            await httpClient.put(`${services.notification_ms}/user/campaigns/${id}`, id);
            mutateFn();
        } catch (error) {
            toast({
                title: t('campaigns.system'),
                position: 'top',
                status: 'error',
                duration: 6000,
                isClosable: true,
                id: 'emptyFields'
            });
        }
    };
    return {
        read
    };
};
export const useMarkNotificationAsRead = ()=>{
    const { httpClient } = useDependencies();
    const { t } = useTranslation('common');
    const toast = useToast();
    const putFunc = async (id, lang, onSuccessEndRequest)=>{
        try {
            await httpClient.put(`${services.notification_ms}/user/notifications/WEB/${id}`, id);
            onSuccessEndRequest();
            mutate([
                `${services.notification_ms}/user/notifications/WEB`,
                lang
            ]);
        } catch (e) {
            toast({
                title: t('notifications.system'),
                position: 'top',
                status: 'error',
                duration: 6000,
                isClosable: true,
                id: 'emptyFields'
            });
        }
    };
    return {
        putFunc
    };
};
export const useGetAdvanceNotificationDetail = (refNo, isOpen)=>{
    const { httpClient } = useServices();
    const { t } = useTranslation('common');
    const toast = useToast();
    const { data, isValidating, error } = useSWR(isOpen ? `${services.e_customs}/avans-kod/notification?refNo=${refNo}` : null, httpClient === null || httpClient === void 0 ? void 0 : httpClient.get, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        shouldRetryOnError: false
    });
    React.useEffect(()=>{
        !!error && toast({
            title: t('notifications.system'),
            position: 'top',
            status: 'error',
            duration: 6000,
            isClosable: true,
            id: 'advance-notification-error'
        });
    }, [
        error
    ]);
    return {
        data,
        isValidating,
        error
    };
};
export const useGetNotificationImage = (param)=>{
    let { imageId, shouldFetch } = param;
    const { httpClient } = useDependencies();
    return useSWR(shouldFetch ? `${services.notification_ms}/images/content/${imageId}` : null, httpClient === null || httpClient === void 0 ? void 0 : httpClient.get);
};
export const useStartLoanOdmApplication = ()=>{
    const { httpClient } = useDependencies();
    const toast = useToast();
    const [isLoanLoading, setIsLoanLoading] = useState(false);
    const [data, setData] = React.useState();
    const { t } = useTranslation('common');
    const fetch = async ()=>{
        const res = await httpClient.post(`${services.loan_orders}?type=LOAN_ODM`, undefined, undefined);
        setData(res);
    };
    const onClickHandler = async (e)=>{
        e.preventDefault();
        e.stopPropagation();
        if (isLoanLoading) {
            return;
        }
        setIsLoanLoading(true);
        try {
            await fetch();
        } catch (error) {
            if (!toast.isActive('tad-request') && error.status !== 400) {
                toast({
                    title: t('common:errors.system'),
                    position: 'top',
                    status: 'error',
                    duration: 6000,
                    isClosable: true,
                    id: 'tad-request'
                });
            }
        } finally{
            setIsLoanLoading(false);
        }
    };
    return {
        data,
        fetch,
        onClickHandler
    };
};
export const useCustomers = ()=>{
    const { httpClient } = useServices();
    return useSWR(`${services.user}/companies`, httpClient === null || httpClient === void 0 ? void 0 : httpClient.get);
};
export const useSetToken = ()=>{
    const { httpClient, axiosService, userService, cookiesService } = useServices();
    const navigate = useNavigate();
    return useSWRMutation(`${services.auth}/token`, (url, param)=>{
        let { arg } = param;
        return httpClient.get(`${url}/${arg.cif}`);
    }, {
        onSuccess: (data)=>{
            cookiesService.set('access', data === null || data === void 0 ? void 0 : data.accessToken, {
                httpOnly: false
            });
            cookiesService.set('refresh', data === null || data === void 0 ? void 0 : data.refreshToken, {
                httpOnly: false
            });
            axiosService.axiosInstance.defaults.headers.authorization = `Bearer ${data === null || data === void 0 ? void 0 : data.accessToken}`;
            userService === null || userService === void 0 ? void 0 : userService.setShouldRefetch();
            navigate(`/dashboard`);
        }
    });
};
